import { ref, watch, computed } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import type { StoreGeneric } from 'pinia';

/**
 * Interface for the sorting method
 *
 * @property label - The display label of the sort method
 * @property value - The value used to identify the sort method
 * @property icon - The icon associated with the sort method
 */
interface SortMethod {
  label: string;
  value: string;
  icon: string;
}

/**
 * Interface for the tree item structure
 *
 * @property id - The unique identifier of the item
 * @property name - The name of the item
 * @property createdAt - The creation date of the item
 * @property updatedAt - The last update date of the item
 * @property parentGroupId - The ID of the parent group, if any
 */
export interface TreeItem {
  id: number;
  name: string;
  createdAt?: string;
  description?: string;
  updatedAt?: string;
  parentGroupId?: number | null; // Undefined for TcmsUserGroupDto compatibility
}

/**
 * Interface for the tree node structure
 *
 * @property name - The name of the node
 * @property originalId - The original ID of the node
 * @property createdAt - The creation date of the node
 * @property updatedAt - The last update date of the node
 * @property children - The child nodes of the current node
 */
export interface TreeNode {
  name: string;
  originalId: number;
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  children: TreeNode[];
}

/**
 * Interface for the draggable tree component
 *
 * @extends ComponentPublicInstance
 * @property openAll - Method to open all nodes in the tree
 * @property closeAll - Method to close all nodes in the tree
 */
export interface DraggableTree extends ComponentPublicInstance {
  openAll: () => void;
  closeAll: () => void;
}

/**
 * Type for the return value of the useTreeOperations' hook
 *
 * @template T - Type of the tree item
 * @property currentSortMethodValue - The current sort method value
 * @property currentSortMethod - The current sort method as a computed property
 * @property sortDirection - The current sort direction ('asc' or 'desc')
 * @property customSortApplied - Flag indicating if custom sorting is applied
 * @property toggleSortDirection - Function to toggle the sort direction
 * @property updateTreeData - Function to update the tree data based on the current items and sort method
 * @property filterItemsHelper - Function to filter items based on a filter string
 * @property filterItemsByIds - Function to filter items based on a list of IDs
 * @property convertItemsToTree - Function to convert flat items to a tree structure
 * @property buildTreeNode - Function to build a tree node and its children
 * @property sortMethods - Array of available sort methods (as computed property)
 * @property sortAlphabetically - Function to sort items alphabetically
 * @property sortByCreationDate - Function to sort items by creation date
 * @property sortByUpdateDate - Function to sort items by update date
 * @property setupTreeDataWatcher - Function to set up a watcher for tree data changes
 */
export interface TreeOperations<T extends TreeItem> {
  currentSortMethodValue: Ref<string>;
  currentSortMethod: ComputedRef<SortMethod>;
  sortDirection: Ref<'asc' | 'desc'>;
  customSortApplied: Ref<boolean>;
  toggleSortDirection: () => void;
  updateTreeData: (
    items: T[] | null,
    sortMethodValue: string,
    sortDir: 'asc' | 'desc',
    isCustomApplied: boolean
  ) => TreeNode[];
  filterItemsHelper: (filter: string, items: T[]) => TreeNode[];
  filterItemsByIds: (itemIds: number[], items: T[]) => TreeNode[];
  convertItemsToTree: (items: T[]) => TreeNode[];
  buildTreeNode: (item: T, allItems: T[]) => TreeNode;
  sortMethods: ComputedRef<SortMethod[]>;
  sortAlphabetically: (items: T[], sortDir: 'asc' | 'desc') => T[];
  sortByCreationDate: (items: T[], sortDir: 'asc' | 'desc') => T[];
  sortByUpdateDate: (items: T[], sortDir: 'asc' | 'desc') => T[];
  setupTreeDataWatcher: (
    store: StoreGeneric,
    itemsRef: Ref<T[] | null>,
    treeDataRef: Ref<TreeNode[]>
  ) => void;
}

/**
 * Custom composable to handle tree operations
 *
 * @template T - Type of the tree item, extending TreeItem
 * @returns TreeOperations<T> - Object containing methods and properties for tree operations
 */
export const useTreeOperations = <T extends TreeItem>(): TreeOperations<T> => {
  const { t } = useI18n();

  // Dropdown options for sorting with translations
  const sortMethods = computed(() => [
    { label: t('sidebar.sort.alphabetically'), value: 'alphabetically', icon: 'pi pi-sort-alpha-down' },
    { label: t('sidebar.sort.creationDate'), value: 'creationDate', icon: 'pi pi-calendar' },
    { label: t('sidebar.sort.lastModified'), value: 'updateDate', icon: 'pi pi-calendar-plus' },
    { label: t('sidebar.sort.custom'), value: 'custom', icon: 'pi pi-sort' }
  ]);

  // Store only the value of the sort method, not the whole object with translated label
  const currentSortMethodValue = ref<string>('alphabetically');

  // Create a computed property that returns the full sort method object with translated label
  const currentSortMethod = computed<SortMethod>(() => {
    return sortMethods.value.find(method => method.value === currentSortMethodValue.value) || sortMethods.value[0];
  });

  const sortDirection = ref<'asc' | 'desc'>('asc');
  const customSortApplied = ref(false);

  /**
   * Toggles the sorting direction between ascending and descending
   */
  const toggleSortDirection = () => {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  };

  /**
   * Sorts items alphabetically by name
   *
   * @param items - The items to sort
   * @param sortDir - The sort direction ('asc' or 'desc')
   * @returns Sorted items
   */
  const sortAlphabetically = (items: T[], sortDir: 'asc' | 'desc'): T[] => {
    return [...items].sort((a, b) => {
      // Handle undefined names and do case-insensitive comparison
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return sortDir === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  };

  /**
   * Sorts items by creation date
   *
   * @param items - The items to sort
   * @param sortDir - The sort direction ('asc' or 'desc')
   * @returns Sorted items
   */
  const sortByCreationDate = (items: T[], sortDir: 'asc' | 'desc'): T[] => {
    return [...items].sort((a, b) => {
      // Handle undefined dates
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Sorts items by update date
   *
   * @param items - The items to sort
   * @param sortDir - The sort direction ('asc' or 'desc')
   * @returns Sorted items
   */
  const sortByUpdateDate = (items: T[], sortDir: 'asc' | 'desc'): T[] => {
    return [...items].sort((a, b) => {
      // Handle undefined dates
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Updates the tree data based on the current items and sort method.
   *
   * @param items - The items to be displayed in the tree
   * @param sortMethodValue - The selected sort method
   * @param sortDir - The sort direction ('asc' or 'desc')
   * @param isCustomApplied - Flag indicating if custom sorting is applied
   * @return The updated tree data
   */
  const updateTreeData = (
    items: T[] | null,
    sortMethodValue: string,
    sortDir: 'asc' | 'desc',
    isCustomApplied: boolean
  ): TreeNode[] => {
    if (!items) {
      return [];
    }

    // If custom sort is applied, and we're in custom mode, don't re-sort the data
    if (isCustomApplied && sortMethodValue === 'custom') {
      console.log('Custom sort applied, returning original items as tree');
      console.log('items:', items);
      return convertItemsToTree(items);
    }

    // Sort items based on the selected method
    let sortedItems: T[];

    switch (sortMethodValue) {
      case 'alphabetically':
        sortedItems = sortAlphabetically(items, sortDir);
        break;
      case 'creationDate':
        sortedItems = sortByCreationDate(items, sortDir);
        break;
      case 'updateDate':
        sortedItems = sortByUpdateDate(items, sortDir);
        break;
      default:
        sortedItems = [...items]; // Fallback to unsorted copy
    }

    // Convert the sorted flat list to a tree structure
    return convertItemsToTree(sortedItems);
  };

  /**
   * Helper function that filters items that start with the filter text
   * If an item matches, all its ancestors are also included
   *
   * @param filter - The filter text
   * @param items - The items to filter
   * @return Filtered tree nodes
   */
  const filterItemsHelper = (filter: string, items: T[]): TreeNode[] => {
    const matchingNodes = new Set<number>();
    const parentsMap = new Map<number, number>();

    // Create a map of nodes to their parent IDs
    items.forEach(item => {
      if (item.parentGroupId && item.id) {
        parentsMap.set(item.id, item.parentGroupId);
      }
    });

    // Find all matching nodes with prefix search
    items.forEach(item => {
      if (item.id && item.name?.toLowerCase().startsWith(filter)) {
        matchingNodes.add(item.id);

        // Add all the ancestor IDs to the set
        let parentId = parentsMap.get(item.id);
        while (parentId) {
          matchingNodes.add(parentId);
          parentId = parentsMap.get(parentId);
        }
      }
    });

    // Convert items to tree structure
    const fullTreeData = convertItemsToTree(items);

    // Return filtered tree data
    return filterTree(fullTreeData, matchingNodes);
  };

  /**
   * Recursively filters the tree nodes based on the matching nodes set
   *
   * @param nodes - The tree nodes to filter
   * @param matchingNodes - The set of matching node IDs
   * @return Filtered tree nodes
   */
  const filterTree = (nodes: TreeNode[], matchingNodes: Set<number>): TreeNode[] => {
    return nodes.filter(node => {
      // Check if the node is in the matching nodes set
      const included = matchingNodes.has(node.originalId);

      // Recursively filter the children
      if (node.children.length) {
        node.children = filterTree(node.children, matchingNodes);
      }

      return included;
    });
  };

  /**
   * Filters items based on a list of ids and
   * returns the complete path of items
   *
   * @param itemIds - Array of ids that should be displayed
   * @param allItems - Items which are available
   * @return Filtered tree with paths to requested items
   */
  const filterItemsByIds = (itemIds: number[], allItems: T[]): TreeNode[] => {
    const matchingNodes = new Set<number>();
    const parentsMap = new Map<number, number>();

    // Create a map of nodes to their parent IDs
    allItems.forEach(item => {
      if (item.parentGroupId !== undefined && item.id) {
        parentsMap.set(item.id, item.parentGroupId as number);
      }
    });

    // Find alle matching nodes and their ancestors
    itemIds.forEach(itemId => {
      matchingNodes.add(itemId);

      // Add all the ancestor IDs to the set
      let parentId = parentsMap.get(itemId);
      while (parentId) {
        matchingNodes.add(parentId);
        parentId = parentsMap.get(parentId);
      }
    });

    // Convert items to tree structure
    const fullTreeData = convertItemsToTree(allItems);

    // Return filtered tree data
    return filterTree(fullTreeData, matchingNodes);
  };

  /**
   * Converts the flat items list to a tree structure
   *
   * @param items - The flat list of items
   * @return Tree structure
   */
  const convertItemsToTree = (items: T[]): TreeNode[] => {
    const rootItems = items.filter(item => !item.parentGroupId);
    return rootItems.map(item => buildTreeNode(item, items));
  };

  /**
   * Recursively builds a tree node and its children
   *
   * @param item - The current item
   * @param allItems - The complete list of items
   * @return The tree node
   */
  const buildTreeNode = (item: T, allItems: T[]): TreeNode => {
    const children = allItems.filter(i => i.parentGroupId === item.id);

    return {
      name: item.name || 'Unbenannt',
      originalId: item.id as number,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      description: item.description,
      children: children.length > 0
        ? children.map(child => buildTreeNode(child, allItems))
        : []
    };
  };

  /**
   * Sets up a watcher for tree data changes
   *
   * @param store - The store containing the data
   * @param itemsRef - The reactive reference to the items array
   * @param treeDataRef - The reactive reference to the tree data
   */
  const setupTreeDataWatcher = (
    store: StoreGeneric,
    itemsRef: Ref<T[] | null>,
    treeDataRef: Ref<TreeNode[]>
  ) => {
    watch(
      () => store.$state,
      () => {
        // If the custom sort is not applied or the current sort method is not 'custom',
        // we update the tree data based on the current items and sort method
        if (!customSortApplied.value || currentSortMethodValue.value !== 'custom') {
          treeDataRef.value = updateTreeData(
            itemsRef.value,
            currentSortMethodValue.value,
            sortDirection.value,
            customSortApplied.value
          );
          return;
        }

        // In custom sort mode, we need to handle deletions and additions of items
        // while preserving the existing tree structure.

        // Create a map to keep track of existing nodes by their original ID
        const existingNodesMap = new Map<number, TreeNode>();

        // Helper function to collect existing nodes into the map
        const collectNodes = (nodes: TreeNode[]) => {
          nodes.forEach(node => {
            existingNodesMap.set(node.originalId, node);
            if (node.children.length > 0) {
              collectNodes(node.children);
            }
          });
        };

        // Collect existing nodes from the current tree data
        collectNodes(treeDataRef.value);

        // New items from the store
        const currentItems = itemsRef.value || [];

        // IDs of current items for quick lookup
        const currentItemIds = new Set(currentItems.map(item => item.id));

        // Function to prune deleted nodes from the tree
        const pruneDeletedNodes = (nodes: TreeNode[]): TreeNode[] => {
          return nodes.filter(node => {
            // Check if the node still exists in the current items
            const stillExists = currentItemIds.has(node.originalId);

            if (stillExists && node.children.length > 0) {
              node.children = pruneDeletedNodes(node.children);
            }

            return stillExists;
          });
        };

        // Create a map to group current items by their parentGroupId
        const parentChildMap = new Map<number | null, number[]>();
        currentItems.forEach(item => {
          const parentId = item.parentGroupId || null;
          if (!parentChildMap.has(parentId)) {
            parentChildMap.set(parentId, []);
          }
          parentChildMap.get(parentId)?.push(item.id);
        });

        // Function to update nodes while preserving custom order
        const updateNodesPreservingOrder = (nodes: TreeNode[], parentId: number | null = null): TreeNode[] => {
          const availableChildIds = parentChildMap.get(parentId) || [];
          const availableChildIdsSet = new Set(availableChildIds);
          const seenSiblingIds = new Set<number>();

          // Update existing nodes in their current order, but keep each child ID only once.
          const updatedExistingNodes = nodes
            .filter(node => {
              if (!availableChildIdsSet.has(node.originalId)) {
                return false;
              }
              if (seenSiblingIds.has(node.originalId)) {
                return false;
              }
              seenSiblingIds.add(node.originalId);
              return true;
            })
            .map(node => {
              const item = currentItems.find(item => item.id === node.originalId);
              if (item) {
                node.name = item.name;
                node.createdAt = item.createdAt;
                node.updatedAt = item.updatedAt;
                node.description = item.description;
              }
              // RECURSIVELY preserve children order
              node.children = updateNodesPreservingOrder(node.children, node.originalId);
              return node;
            });
          
          // Find new nodes that weren't in the tree before
          const existingNodeIds = new Set(updatedExistingNodes.map(node => node.originalId));
          const newNodeIds = availableChildIds.filter(id => !existingNodeIds.has(id));
          
          // Build new nodes recursively
          const buildNewNode = (nodeId: number): TreeNode => {
            const item = currentItems.find(item => item.id === nodeId);
            if (!item) {
              throw new Error(`Item with id ${nodeId} not found`);
            }
            
            const childIds = parentChildMap.get(nodeId) || [];
            const children = childIds.map(childId => buildNewNode(childId));
            
            return {
              name: item.name,
              originalId: item.id as number,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
              description: item.description,
              children
            };
          };
          
          const newNodes = newNodeIds.map(id => buildNewNode(id));
          
          // Return: existing nodes in custom order + new nodes at end
          return [...updatedExistingNodes, ...newNodes];
        };

        // Prune deleted nodes from the current tree data
        treeDataRef.value = pruneDeletedNodes(treeDataRef.value);

        // Update tree while preserving custom order at all levels
        treeDataRef.value = updateNodesPreservingOrder(treeDataRef.value, null);
      },
      { deep: true }
    );
  };

  return {
    currentSortMethodValue,
    currentSortMethod,
    sortDirection,
    customSortApplied,
    toggleSortDirection,
    updateTreeData,
    filterItemsHelper,
    filterItemsByIds,
    convertItemsToTree,
    buildTreeNode,
    sortMethods,
    sortAlphabetically,
    sortByCreationDate,
    sortByUpdateDate,
    setupTreeDataWatcher
  };
};
